import { Badge } from "@/projects/NPS/Stepper/components/ui/badge";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/projects/NPS/Stepper/components/ui/card";
import { ArrowRight } from "lucide-react";

export const InvestmentOfferings = () => {
  // const { tenantId } = useTenant();
  // if (!tenantId) {
  //   return null;
  // }
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section className="relative bg-custom-radial-gradient py-20">
      <div className="overlayblur" />
      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mx-auto mb-4 max-w-2xl text-center">
          <Badge
            variant="outline"
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-none border-[rgba(29,171,145,0.2)] bg-[linear-gradient(to_right,rgba(29,171,145,0.1),rgba(10,52,74,0.1))] px-4 py-2 text-[12px] font-semibold text-secondary"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1DAB91"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-sparkles h-4 w-4 text-primary"
            >
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              <path d="M20 3v4" />
              <path d="M22 5h-4" />
              <path d="M4 17v2" />
              <path d="M5 18H3" />
            </svg>
            Investment Options
          </Badge>
        </div>
        <div className="mx-auto mb-4 text-center">
          <h2 className="font-nunito text-2xl font-bold leading-tight text-black md:text-3xl lg:text-4xl">
            Our Investment Offerings
          </h2>
        </div>
        <div className="mx-auto mb-24 text-center">
          <p className="mx-auto max-w-2xl font-nunito text-base leading-relaxed text-muted-foreground md:text-lg">
            Access diverse investment options tailored to your financial goals.
            Start your wealth creation journey today.{" "}
          </p>
        </div>
        <div className="overlaygrayblur" />
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto grid max-w-[1152px] grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
        >
          {[
            {
              title: "IPO",
              desc: "Get early access to companies going public. Invest in Initial Public Offerings and be part of the growth journey.",
              icon: "/assets/Ipo.svg", // Use relative path from public directory
              stat: "5+   Open IPOs",
              link: `/ipo`,
              iconBg: "bg-green-50 text-green-600",
              badge: "Popular",
            },
            {
              title: "Bonds",
              desc: "Secure, fixed-income investments with predictable returns. Diversify your portfolio with AAA-rated bonds.",
              icon: "/assets/Bonds.svg", // Use relative path from public directory
              stat: "Up to 9.5% Yield",
              iconBg: "bg-red-50 text-white",
              link: `/bonds`,
            },
            {
              title: "Fixed Deposits",
              desc: "DICGC insured FDs from trusted banks and NBFCs. Earn up to 9.10% with complete safety.",
              icon: "/assets/FixedDeposits.svg", // Use relative path from public directory
              stat: "Up to 9.10% p.a.",
              iconBg: "bg-blue-50 text-blue-600",
              badge: "Trending",
              link: `/fds`,
            },
            {
              title: "NPS",
              desc: "Plan your retirement with National Pension System. Tax benefits under Section 80C and 80CCD.",
              icon: "/assets/Nps.svg", // Use relative path from public directory
              stat: "Tax Savings",
              iconBg: "bg-purple-50 text-purple-600",
              link: `/nps`,
            },
            {
              title: "Calculator",
              desc: "Plan your investments with our FD, SIP, and Lumpsum calculators. Make informed financial decisions.",
              icon: "/assets/Calculator.svg", // Use relative path from public directory
              stat: "Free Tools",
              iconBg: "bg-pink-50 text-pink-600",
              link: `/calculators`,
            },
          ].map(item => (
            <motion.div key={item.title} variants={itemVariants}>
              <Card className="group relative cursor-pointer overflow-hidden rounded-2xl border border-[rgba(229,232,235,0.5)] bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(29,171,145,0.3)] hover:shadow-2xl md:p-8">
                {/* Hover Overlay */}
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(29,171,145,0.05),transparent)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                <CardContent className="relative p-0">
                  {/* Badge */}
                  {item.badge && (
                    <div className="absolute right-2 rounded-full border border-[rgba(29,171,145,0.2)] bg-[linear-gradient(to_right,rgba(29,171,145,0.2),rgba(29,171,145,0.1))] px-3 py-[6px] font-nunito text-[10px] font-bold uppercase tracking-wider text-green-600">
                      {item.badge}
                    </div>
                  )}

                  {/* Icon */}
                  <div
                    className={`mb-6 flex h-16 w-16 items-center justify-center rounded-xl shadow-sm transition-transform duration-300 group-hover:scale-110 ${item.iconBg} `}
                  >
                    <img src={item.icon} alt={item.title} className="h-8 w-8" />
                  </div>

                  {/* Title */}
                  <h3 className="mb-3 font-nunito text-xl font-bold text-black group-hover:text-primary">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="mb-6 font-nunito text-sm leading-relaxed text-muted-foreground">
                    {item.desc}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between border-t border-[rgba(229,232,235,0.5)] pt-5">
                    {/* Stat */}
                    <span className="rounded-full bg-[linear-gradient(to_right,rgba(10,52,74,0.1),rgba(10,52,74,0.05))] px-4 py-2 font-nunito text-[12px] font-bold text-black">
                      {item.stat}
                    </span>

                    {/* CTA */}
                    <div className="flex translate-x-2 items-center gap-1 font-nunito text-sm font-semibold text-green-600 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100">
                      Explore{" "}
                      <ArrowRight stroke="#1DAB91" className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
